import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, Lock, Loader2, Tag, GraduationCap, CalendarClock } from "lucide-react";

const GRADES = ["K", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];
const GRADE_LABEL = (g) => (g === "K" ? "Kindergarten" : `Grade ${g}`);

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [gradeLevel, setGradeLevel] = useState("K");
  const [studentName, setStudentName] = useState("");
  const [studentCount, setStudentCount] = useState(1);
  const [annualCode, setAnnualCode] = useState("");
  const [monthlyCode, setMonthlyCode] = useState("");
  const [preview, setPreview] = useState({});
  const [busy, setBusy] = useState("");

  const membershipTotal = (49.99 + 29.99 * (studentCount - 1)).toFixed(2);

  const validate = async (plan) => {
    const code = plan === "annual" ? annualCode : monthlyCode;
    if (!code) { toast.error("Enter a discount code first"); return; }
    try {
      const { data } = await api.post("/discount/validate", { code, plan, student_count: studentCount });
      setPreview((p) => ({ ...p, [plan]: data }));
      toast.success(`Code applied — you save $${data.discount.toFixed(2)}`);
    } catch (e) {
      setPreview((p) => ({ ...p, [plan]: null }));
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const checkout = async (plan) => {
    if (!user) { navigate("/login"); return; }
    setBusy(plan);
    try {
      const origin_url = window.location.origin;
      let res;
      if (plan === "annual") {
        if (!studentName.trim()) { toast.error("Enter the student's name"); setBusy(""); return; }
        res = await api.post("/checkout/enrollment", {
          grade_level: gradeLevel, student_name: studentName,
          discount_code: annualCode || null, origin_url,
        });
      } else {
        res = await api.post("/checkout/subscription", {
          student_count: studentCount, discount_code: monthlyCode || null, origin_url,
        });
      }
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy("");
    }
  };

  const annualPrice = preview.annual ? preview.annual.final_amount.toFixed(2) : "349.00";
  const monthlyPrice = preview.monthly ? preview.monthly.final_amount.toFixed(2) : membershipTotal;

  return (
    <div className="mx-auto max-w-6xl px-5 py-16 lg:px-8">
      <div className="text-center">
        <span className="label-caps text-primary">Enrollment</span>
        <h1 className="mt-2 font-head text-4xl font-black sm:text-5xl">Choose Your Path</h1>
        <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">One flat year of full-grade enrollment, or a flexible monthly family membership. Every plan includes the AI Tutor.</p>
      </div>

      <div className="mt-14 grid gap-px border border-border bg-border lg:grid-cols-2">
        {/* ANNUAL */}
        <div className="flex flex-col bg-card p-8 lg:p-10">
          <div className="flex items-center gap-2 text-primary"><GraduationCap className="h-5 w-5" /><span className="label-caps">Annual Enrollment</span></div>
          <div className="mt-5 flex items-end gap-2">
            <span className="font-head text-6xl font-black text-foreground">${annualPrice}</span>
            <span className="mb-2 text-sm text-muted-foreground">/ student / year</span>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">One student, one grade level, one full year of complete access. Extension available if a little more time is needed.</p>

          <ul className="mt-6 space-y-3 text-sm">
            {["Full year access to one grade level", "Every lesson, assignment & assessment", "AI Tutor with voice & speech", "Extension enrollment on request"].map((f) => (
              <li key={f} className="flex items-start gap-3"><Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />{f}</li>
            ))}
          </ul>

          <div className="mt-8 space-y-4 border-t border-border pt-6">
            <div>
              <Label className="label-caps text-muted-foreground">Grade Level</Label>
              <Select value={gradeLevel} onValueChange={setGradeLevel}>
                <SelectTrigger className="mt-2 rounded-none border-border bg-background" data-testid="annual-grade-select"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  {GRADES.map((g) => <SelectItem key={g} value={g}>{GRADE_LABEL(g)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-caps text-muted-foreground">Student Name</Label>
              <Input value={studentName} onChange={(e) => setStudentName(e.target.value)} placeholder="e.g. Cole Hornsby" data-testid="annual-student-name" className="mt-2 rounded-none border-border bg-background" />
            </div>
            <div>
              <Label className="label-caps text-muted-foreground">Discount Code</Label>
              <div className="mt-2 flex gap-2">
                <Input value={annualCode} onChange={(e) => setAnnualCode(e.target.value)} placeholder="Optional" data-testid="annual-discount-input" className="rounded-none border-border bg-background" />
                <Button type="button" variant="outline" onClick={() => validate("annual")} className="rounded-none" data-testid="annual-apply-code"><Tag className="h-4 w-4" /></Button>
              </div>
            </div>
          </div>

          <Button onClick={() => checkout("annual")} disabled={busy === "annual"} className="mt-8 w-full rounded-none label-caps bg-primary py-6 text-base hover:bg-primary/80" data-testid="annual-checkout-btn">
            {busy === "annual" ? <Loader2 className="h-5 w-5 animate-spin" /> : `Enroll — $${annualPrice}`}
          </Button>
        </div>

        {/* MONTHLY */}
        <div className="flex flex-col bg-card p-8 lg:p-10">
          <div className="flex items-center gap-2 text-primary"><CalendarClock className="h-5 w-5" /><span className="label-caps">Monthly Membership</span></div>
          <div className="mt-5 flex items-end gap-2">
            <span className="font-head text-6xl font-black text-foreground">${monthlyPrice}</span>
            <span className="mb-2 text-sm text-muted-foreground">/ month</span>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">$49.99 for your first student, $29.99 for each additional student. Billed automatically every month — cancel anytime.</p>

          <ul className="mt-6 space-y-3 text-sm">
            {["Access to the entire K-12 library", "Unlocks premium high-school electives", "Multiple students on one plan", "AI Tutor for the whole family"].map((f) => (
              <li key={f} className="flex items-start gap-3"><Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />{f}</li>
            ))}
          </ul>

          <div className="mt-8 space-y-4 border-t border-border pt-6">
            <div>
              <Label className="label-caps text-muted-foreground">Number of Students</Label>
              <div className="mt-2 flex items-center gap-3">
                <Button type="button" variant="outline" className="rounded-none" onClick={() => setStudentCount(Math.max(1, studentCount - 1))} data-testid="student-minus">−</Button>
                <span className="w-12 text-center font-head text-2xl font-bold" data-testid="student-count">{studentCount}</span>
                <Button type="button" variant="outline" className="rounded-none" onClick={() => setStudentCount(studentCount + 1)} data-testid="student-plus">+</Button>
              </div>
            </div>
            <div>
              <Label className="label-caps text-muted-foreground">Discount Code</Label>
              <div className="mt-2 flex gap-2">
                <Input value={monthlyCode} onChange={(e) => setMonthlyCode(e.target.value)} placeholder="Optional" data-testid="monthly-discount-input" className="rounded-none border-border bg-background" />
                <Button type="button" variant="outline" onClick={() => validate("monthly")} className="rounded-none" data-testid="monthly-apply-code"><Tag className="h-4 w-4" /></Button>
              </div>
            </div>
          </div>

          <Button onClick={() => checkout("monthly")} disabled={busy === "monthly"} className="mt-8 w-full rounded-none label-caps bg-primary py-6 text-base hover:bg-primary/80" data-testid="monthly-checkout-btn">
            {busy === "monthly" ? <Loader2 className="h-5 w-5 animate-spin" /> : `Start Membership — $${monthlyPrice}/mo`}
          </Button>
        </div>
      </div>

      <div className="mt-10 flex items-center justify-center gap-2 text-sm text-muted-foreground">
        <Lock className="h-4 w-4" /> Secure checkout via Stripe. Premium high-school electives require an active membership or matching enrollment.
      </div>
    </div>
  );
}
